import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  emptySchoolSelection,
  schools,
  staff,
  type AgeFilter,
  type EventKind,
  type School,
} from "../domain/data";
import { ExtraQuestionsEditor } from "../components/QuestionDesign";
import { useEvents } from "../state/events";
import { questionDesignError, type FormQuestion } from "../domain/questions";
import { ErrorFlash } from "../components/ErrorFlash";
import { PrototypeBanner, StaffNav } from "../components/ui";

function formatClock(hhmm: string): { clock: string; suffix: string } {
  const [hourRaw, minuteRaw] = hhmm.split(":");
  const hour = Number(hourRaw);
  const minute = Number(minuteRaw);
  const suffix = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 || 12;
  return {
    clock: `${hour12}:${String(minute).padStart(2, "0")}`,
    suffix,
  };
}

function formatTimeLabel(start: string, end: string): string {
  const from = formatClock(start);
  const to = formatClock(end);
  if (from.suffix === to.suffix) {
    return `${from.clock}-${to.clock} ${from.suffix}`;
  }
  return `${from.clock} ${from.suffix}-${to.clock} ${to.suffix}`;
}

export function StaffCreateEvent() {
  const navigate = useNavigate();
  const { addEvent } = useEvents();
  const [name, setName] = useState("");
  const [date, setDate] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [location, setLocation] = useState("");
  const [capacity, setCapacity] = useState("40");
  const [allSchools, setAllSchools] = useState(true);
  const [selected, setSelected] = useState<Record<School, boolean>>(
    emptySchoolSelection(),
  );
  const [kind, setKind] = useState<EventKind>("rsvp");
  const [age, setAge] = useState<AgeFilter>("all");
  const [questions, setQuestions] = useState<FormQuestion[]>([]);
  const [description, setDescription] = useState("");
  const [descriptionEs, setDescriptionEs] = useState("");
  const [error, setError] = useState("");

  function toggleSchool(school: School) {
    setAllSchools(false);
    setSelected((current) => ({ ...current, [school]: !current[school] }));
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    const trimmedName = name.trim();
    const trimmedLocation = location.trim();
    const cap = Number(capacity);
    const picked = schools.filter((school) => selected[school]);

    if (!trimmedName || !date || !start || !end || !trimmedLocation) {
      setError("Fill in name, date, time, and location.");
      return;
    }
    if (!Number.isFinite(cap) || cap < 1) {
      setError("Capacity must be at least 1.");
      return;
    }
    if (!allSchools && picked.length === 0) {
      setError("Choose all Lift schools or at least one school.");
      return;
    }
    if (kind === "rsvp") {
      const extraError = questionDesignError(questions);
      if (extraError) {
        setError(extraError);
        return;
      }
    }

    const created = addEvent({
      name: trimmedName,
      dateIso: date,
      timeLabel: formatTimeLabel(start, end),
      location: trimmedLocation,
      capacity: Math.floor(cap),
      schools: allSchools ? "all" : picked,
      age,
      kind,
      questions: kind === "rsvp" ? questions : [],
      description,
      descriptionEs,
    });
    navigate(
      kind === "program"
        ? `/staff/events/${created.id}?tab=attendance`
        : `/staff/events/${created.id}`,
    );
  }

  return (
    <div className="app">
      <PrototypeBanner />
      <main className="shell">
        <StaffNav />
        <p className="meta">
          Staff · {staff.given} {staff.family} · new event
        </p>
        <h1>Create event</h1>
        <p className="lede">
          Afterschool programs stay staff-only: take attendance by date on one
          event. RSVP trips are what families see, then archive after the date.
          Set an age so high schoolers do not see middle school events, and the
          other way around.
        </p>

        <form className="stack" onSubmit={onSubmit}>
          <fieldset className="field">
            <legend>Type</legend>
            <div className="checks">
              <label>
                <input
                  type="radio"
                  name="event-kind"
                  checked={kind === "program"}
                  onChange={() => setKind("program")}
                />
                Afterschool program (staff attendance, not listed for families)
              </label>
              <label>
                <input
                  type="radio"
                  name="event-kind"
                  checked={kind === "rsvp"}
                  onChange={() => setKind("rsvp")}
                />
                RSVP trip (families see this until the date passes)
              </label>
            </div>
          </fieldset>
          <div className="field">
            <label htmlFor="event-name">Name</label>
            <input
              id="event-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="grid-2">
            <div className="field">
              <label htmlFor="event-date">Date</label>
              <input
                id="event-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="event-capacity">Capacity (van seats, etc.)</label>
              <input
                id="event-capacity"
                type="number"
                min={1}
                value={capacity}
                onChange={(e) => setCapacity(e.target.value)}
                required
              />
              {kind === "rsvp" ? (
                <p className="anno">
                  When these seats are full, families join a waitlist. If someone cancels, the first person waiting is registered.
                </p>
              ) : null}
            </div>
          </div>
          <div className="grid-2">
            <div className="field">
              <label htmlFor="event-start">Start</label>
              <input
                id="event-start"
                type="time"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="event-end">End</label>
              <input
                id="event-end"
                type="time"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                required
              />
            </div>
          </div>
          <div className="field">
            <label htmlFor="event-location">Location</label>
            <input
              id="event-location"
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              required
            />
          </div>
          <div className="field">
            <label htmlFor="event-description">Description</label>
            <textarea
              id="event-description"
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Students attending this beach day have to be strong swimmers."
            />
          </div>
          <div className="field">
            <label htmlFor="event-description-es">Description (Spanish)</label>
            <textarea
              id="event-description-es"
              rows={4}
              value={descriptionEs}
              onChange={(e) => setDescriptionEs(e.target.value)}
              placeholder="Los estudiantes que asistan a este día de playa deben ser buenos nadadores."
            />
            <div className="anno">
              Optional. Spanish-speaking families see this when they switch the
              site to Español.
            </div>
          </div>
          <fieldset className="field">
            <legend>Age</legend>
            <div className="checks">
              <label>
                <input
                  type="radio"
                  name="event-age"
                  checked={age === "all"}
                  onChange={() => setAge("all")}
                />
                All ages
              </label>
              <label>
                <input
                  type="radio"
                  name="event-age"
                  checked={age === "ms"}
                  onChange={() => setAge("ms")}
                />
                Middle school (6th-8th)
              </label>
              <label>
                <input
                  type="radio"
                  name="event-age"
                  checked={age === "hs"}
                  onChange={() => setAge("hs")}
                />
                High school (9th-12th)
              </label>
            </div>
          </fieldset>
          <fieldset className="field">
            <legend>Schools</legend>
            <div className="checks">
              <label>
                <input
                  type="checkbox"
                  checked={allSchools}
                  onChange={() => {
                    setAllSchools(true);
                    setSelected(emptySchoolSelection());
                  }}
                />
                All Lift schools
              </label>
              {schools.map((school) => (
                <label key={school}>
                  <input
                    type="checkbox"
                    checked={!allSchools && selected[school]}
                    onChange={() => toggleSchool(school)}
                  />
                  {school}
                </label>
              ))}
            </div>
          </fieldset>

          {kind === "rsvp" ? (
            <section className="design-section">
              <div className="wire design-section-head">
                <div>
                  <div className="meta">Families answer these when they RSVP</div>
                  <strong>Event questions</strong>
                </div>
                <div className="tag muted">{questions.length} questions</div>
              </div>
              <p className="anno" style={{ margin: "0.5rem 0 0" }}>
                Add a Spanish question and choices so Español families see them.
                English is used if Spanish is blank.
              </p>
              <ExtraQuestionsEditor
                questions={questions}
                emptyLabel="No extra questions on this trip"
                onChange={setQuestions}
              />
            </section>
          ) : null}

          {error ? <ErrorFlash key={error} message={error} /> : null}

          <div className="actions">
            <button type="submit">Save event</button>
            <Link className="btn ghost" to="/staff/events">
              Cancel
            </Link>
          </div>
        </form>
      </main>
    </div>
  );
}
